/**
 * Where to land after signing in.
 *
 * FRIDAY emails deep links constantly: a contact on a booking notification, a card in the
 * inbox digest, a brief. Every one of them hits the login screen when the session has
 * expired, and this decides whether the reader then sees the thing they clicked or the
 * home page.
 *
 * WHY THIS IS NOT ROUTER STATE. AuthGuard records the intended page in `location.state`
 * and that survives exactly as long as the tab's JavaScript does. Signing in is
 * `msal.loginRedirect`, a full page navigation to login.microsoftonline.com and back to
 * /auth/callback, so by the time anybody is authenticated the router state is gone and
 * AuthCallback had nothing to go on but a hard-coded '/'. Every emailed link therefore
 * ended on the Today page. sessionStorage is the one store that survives a redirect to
 * another origin and back, and dies with the tab, which is the right lifetime for
 * "the page I was trying to open just now".
 */

const KEY = 'friday.return_path'

/**
 * Only same-site paths are honoured.
 *
 * A protocol-relative "//evil.example" is still a valid pathname to the router and would
 * send a freshly authenticated user off-site. Anything that is not a single leading slash
 * falls back home, as does /login itself, which would otherwise bounce.
 */
export function safeReturnPath(from: unknown): string {
  const path = typeof from === 'string'
    ? from
    : (from as { pathname?: string; search?: string } | null)?.pathname
      ? `${(from as { pathname: string; search?: string }).pathname}${(from as { search?: string }).search ?? ''}`
      : null
  if (!path || !path.startsWith('/') || path.startsWith('//')) return '/'
  if (path === '/login' || path.startsWith('/login?')) return '/'
  return path
}

/**
 * Record the page somebody was trying to reach, before sending them to sign in.
 *
 * Validated on the way in as well as on the way out. A value that would never be honoured
 * is not worth storing, and storing only what we would act on keeps the two ends from
 * disagreeing.
 */
export function rememberReturnPath(from: unknown): void {
  const path = safeReturnPath(from)
  try {
    if (path === '/') sessionStorage.removeItem(KEY)
    else sessionStorage.setItem(KEY, path)
  } catch {
    // Private-mode Safari and similar. Losing the deep link is a worse landing page,
    // not a broken sign-in, so this must never throw into the auth flow.
  }
}

/**
 * Read the recorded page and forget it.
 *
 * Read-once on purpose. Leaving it behind would send the NEXT sign-in in this tab to a
 * page the person had already finished with.
 */
export function takeReturnPath(): string {
  let stored: string | null = null
  try {
    stored = sessionStorage.getItem(KEY)
    sessionStorage.removeItem(KEY)
  } catch {
    return '/'
  }
  return safeReturnPath(stored)
}
